import { observer } from 'mobx-react-lite'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useMemo, useState } from 'react'
import Card from '@mui/material/Card'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import ButtonWithProgress from 'reusable/mui/progress-button/ButtonWithProgress'
import SkeletonLoader from 'reusable/mui/skeleton/SkeletonLoader'
import { themeConfig }from '@gap/shared/theme'
import Chip, { ChipProps } from '@mui/material/Chip'
import Fade from '@mui/material/Fade'
import SyncOutlined from '@mui/icons-material/SyncOutlined'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import IntegrationCapabilityRow, { IntegrationCapabilityRowProps } from './IntegrationCapabilityRow'

export type IntegrationStatus = 'connected' | 'not connected' | 'connection lost'

/** The skeleton's height while the card loads, sized to what the card will render. */
function skeletonHeight(params: { rowCount: number, hasFooter: boolean }): number {
   const rowsHeight = params.rowCount ? 12 + params.rowCount * 36 : 0
   return 78.5 + rowsHeight + (params.hasFooter ? 47.5 : 0)
}

/** Label for the hover action button, naming the action its click performs. */
function statusActionLabel(params: { status: Exclude<IntegrationStatus, 'not connected'>, loading: boolean, disconnectOnly: boolean }): string {
   if (params.status === 'connected' || params.disconnectOnly) return params.loading ? 'Disconnecting' : 'Disconnect'
   return params.loading ? 'Connecting' : 'Re-connect'
}

/** What a card can do about the org's client source, wired by the surface that owns the integration. */
export interface ClientSourceActions {
   isCurrent: boolean
   onSyncNow: () => void
   syncing: boolean
   onSetAsSource: () => void
   settingSource: boolean
}

export interface AdvisorIntegrationCardProps {
   icon: React.ReactNode
   title: string
   subTitle: string
   status: IntegrationStatus
   onClick: () => void
   loading?: boolean
   /**
    * When provided and status is 'connected', replaces the chip + hover-disconnect
    * affordance with a button action (e.g. "Sync now").
    */
   connectedAction?: {
      label: string
      onClick: () => void
      loading?: boolean
   }
   /** Create client modal only: once connected, the main row shows whether this is the org's client source and offers its action. */
   clientSource?: ClientSourceActions
   /** What the integration can do, one row each, under the main row. */
   rows?: IntegrationCapabilityRowProps[]
   /** One informational line in place of the rows, e.g. while another integration holds the client source. */
   note?: string
   /** Extra controls rendered under a hairline below the rows, e.g. a per-integration setting. */
   footer?: React.ReactNode
   /**
    * Pending state for the Connect button while a redirect is in flight. `loading` shows a spinner
    * and blocks repeat clicks; `disabled` greys it out while a sibling card is mid-connect.
    */
   connectAction?: {
      loading?: boolean
      disabled?: boolean
   }
   /**
    * Pending state for the hover-revealed action button (Disconnect, or Re-connect after a lost
    * connection). `loading` pins the button visible, since it would otherwise vanish the moment
    * the pointer left, and shows a spinner in place of the label.
    */
   statusAction?: {
      loading?: boolean
      disabled?: boolean
   }
   /**
    * When true, the hover-revealed action button reads Disconnect whatever the status, for a card
    * whose `onClick` only ever removes the integration. Omitted, the label follows the status.
    */
   disconnectOnly?: boolean
}

export default observer(function AdvisorIntegrationCard(props: AdvisorIntegrationCardProps) {
   const [hovered, setHovered] = useState(false)
   const onMouseEnter = () => setHovered(true)
   const onMouseLeave = () => setHovered(false)

   const rowCount = props.note ? 1 : props.rows?.length ?? 0

   return <SkeletonLoader loading={props.loading || false} sx={{ height: skeletonHeight({ rowCount, hasFooter: !!props.footer }), transform: 'none', borderRadius: '16px' }}>
      <Card
         sx={{
            p: 2,
            borderRadius: '16px',
            border: `1px solid ${themeConfig.primary[200]}`,
            ':hover': {
               backgroundColor: themeConfig.primary[100],
            }
         }}
         elevation={0}
         onMouseEnter={onMouseEnter}
         onMouseLeave={onMouseLeave}
      >
         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
               {props.icon}
               <Box sx={{ ml: 2 }}>
                  <Typography fontWeight={600}>{props.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                     {props.subTitle}
                  </Typography>
               </Box>
            </Box>

            {props.status !== 'not connected' ? (
               <ConnectedSlot
                  hovered={hovered}
                  status={props.status}
                  onClick={props.onClick}
                  connectedAction={props.connectedAction}
                  clientSource={props.clientSource}
                  statusAction={props.statusAction}
                  disconnectOnly={props.disconnectOnly}
               />
            ) : <ButtonWithProgress
               variant="text"
               loading={props.connectAction?.loading}
               disabled={props.connectAction?.disabled}
               onClick={props.onClick}
            >
               {props.connectAction?.loading ? 'Connecting' : 'Connect'}
            </ButtonWithProgress>}
         </Box>

         {rowCount > 0 && <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {props.note
               ? <IntegrationCapabilityRow id="note" icon={<InfoOutlined />} description={props.note} />
               : props.rows?.map(row => <IntegrationCapabilityRow key={row.id} {...row} />)}
         </Box>}

         {props.footer && <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${themeConfig.primary[200]}` }}>{props.footer}</Box>}
      </Card>
   </SkeletonLoader>
})

type ConnectedSlotProps = { hovered: boolean, status: Exclude<IntegrationStatus, 'not connected'> } & Pick<AdvisorIntegrationCardProps, 'onClick' | 'connectedAction' | 'clientSource' | 'statusAction' | 'disconnectOnly'>

const ConnectedSlot = observer(function ConnectedSlot(props: ConnectedSlotProps) {
   if (props.status === 'connected' && props.clientSource) {
      return <ClientSourceFade hovered={props.hovered} {...props.clientSource} />
   }
   if (props.connectedAction) {
      return <ConnectedActionButton {...props.connectedAction} />
   }
   return <SmoothChipToButtonFade hovered={props.hovered} status={props.status} onClick={props.onClick} action={props.statusAction} disconnectOnly={props.disconnectOnly} />
})

const ConnectedActionButton = observer(function ConnectedActionButton(props: { label: string, onClick: () => void, loading?: boolean }) {
   if (props.loading) {
      return <CircularProgress size={20} />
   }
   return <Button variant="text" onClick={props.onClick}>{props.label}</Button>
})

// Both states share one grid cell, so the slot is as wide as its widest state and the labels never wrap.
const actionSlotSx = { gridArea: '1 / 1', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, whiteSpace: 'nowrap' } as const

/** Rests on the client source state and swaps to its action on hover; the action stays while its request runs. */
const ClientSourceFade = observer(function ClientSourceFade(props: { hovered: boolean } & ClientSourceActions) {
   const loading = props.isCurrent ? props.syncing : props.settingSource
   const showButton = props.hovered || loading

   return <Box sx={{ display: 'grid', alignItems: 'center', justifyItems: 'end' }}>
      <Fade in={!showButton} timeout={300} unmountOnExit>
         <Box sx={actionSlotSx}>
            {props.isCurrent
               ? <CurrentClientSourceLabel />
               : <Chip label="Connected" size="small" sx={{ backgroundColor: themeConfig.system.success[300] }} />}
         </Box>
      </Fade>
      <Fade in={showButton} timeout={300} unmountOnExit>
         <Box sx={actionSlotSx}>
            {props.isCurrent
               ? <ButtonWithProgress variant="text" onClick={props.onSyncNow} loading={props.syncing} icon={<SyncOutlined />} sx={{ minWidth: 'fit-content' }}>
                  {props.syncing ? 'Syncing clients' : 'Sync now'}
               </ButtonWithProgress>
               : <ButtonWithProgress variant="text" onClick={props.onSetAsSource} loading={props.settingSource} sx={{ minWidth: 'fit-content' }}>
                  Set as client source
               </ButtonWithProgress>}
         </Box>
      </Fade>
   </Box>
})

/** The green dot and label a card shows while its integration is the org's client source. */
export function CurrentClientSourceLabel() {
   return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: themeConfig.system.success[600], flexShrink: 0 }} />
      <Typography variant="body2" sx={{ color: themeConfig.system.success[600] }}>Current client source</Typography>
   </Box>
}

const SmoothChipToButtonFade = observer(function SmoothChipToButtonFade(props: { hovered: boolean, status: Exclude<IntegrationStatus, 'not connected'>, onClick: () => void, action?: { loading?: boolean, disabled?: boolean }, disconnectOnly?: boolean }) {
   // Keep the button up while its request runs, so the feedback survives the pointer moving away.
   const showButton = props.hovered || !!props.action?.loading

   const chipProps: ChipProps = useMemo(() => {
      switch (props.status) {
         case 'connected':
            return {
               label: 'Connected',
               sx: {
                  backgroundColor: themeConfig.system.success[300]
               }
            }
         case 'connection lost':
            return {
               label: 'Connection lost',
               sx: {
                  backgroundColor: themeConfig.system.warning[300]
               }
            }

      }
   }, [props.status])

   return <Box sx={{ position: 'relative', minWidth: 130, mt: -3, display: 'inline-flex' }}>
      <Fade in={!showButton} timeout={300} unmountOnExit>
         <Box
            sx={{
               position: 'absolute',
               top: 0,
               left: 0,
               right: 0,
               display: 'flex',
               justifyContent: 'flex-end',
            }}
         >
            <Chip
               {...chipProps}
               size="small"
            />
         </Box>
      </Fade>
      <Fade in={showButton} timeout={300} unmountOnExit>
         <Box
            sx={{
               position: 'absolute',
               top: -6,
               left: 0,
               right: 0,
               display: 'flex',
               justifyContent: 'flex-end',
            }}
         >
            <ButtonWithProgress
               variant="text"
               onClick={props.onClick}
               size="small"
               loading={props.action?.loading}
               disabled={props.action?.disabled}
               sx={{ minWidth: 'fit-content', }}
            >
               {statusActionLabel({ status: props.status, loading: !!props.action?.loading, disconnectOnly: !!props.disconnectOnly })}
            </ButtonWithProgress>
         </Box>
      </Fade>
   </Box>
})
